import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';
import type { ReactNode } from 'react';

interface HeroProps {
  badge: string;
  headline: string | ReactNode;
  description: string | ReactNode;
  heroVisuals?: ReactNode;
  secondaryDescription?: string;
  children?: ReactNode;
  className?: string;
}

const Hero = ({
  badge,
  headline,
  description,
  secondaryDescription,
  children,
  heroVisuals,
  className = ''
}: HeroProps) => {
  return (
    <section className={`relative overflow-hidden py-16 pt-8 lg:pt-16 ${className}`}>
      <div className="absolute inset-0 gradient-subtle"></div>
      <div className={cn("relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-4 pt-8", heroVisuals && "grid lg:grid-cols-[1fr_1fr] lg:gap-8 lg:flex-row items-center")}>
        <div className={cn("space-y-12", !heroVisuals && "mx-auto text-center")}>
          {/* Main Content */}
          <div className={cn("space-y-6 max-w-5xl", !heroVisuals && "mx-auto")}>
            {/* Badge - H1 for SEO */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium tracking-wide uppercase"
            >
              {badge}
            </motion.h1>

            {/* Main Headline - H2 for clarity */}
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1, ease: "easeOut" }}
              className={cn("text-5xl md:text-7xl font-medium md:leading-tight font-sans text-foreground", heroVisuals && "text-5xl md:text-6xl")}
            >
              {headline}
            </motion.h2>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2, ease: "easeOut" }}
              className={cn("text-xl md:text-2xl text-muted-foreground max-w-4xl leading-relaxed", !heroVisuals && "mx-auto")}
            >
              {description}
            </motion.p>

            {/* Secondary Description (optional) */}
            {secondaryDescription && (
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3, ease: "easeOut" }}
                className={cn("text-xl md:text-2xl text-muted-foreground max-w-4xl leading-relaxed", !heroVisuals && "mx-auto")}
              >
                {secondaryDescription}
              </motion.p>
            )}
          </div>

          {/* CTA Buttons / Children */}
          {children && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4, ease: "easeOut" }}
              className={cn("flex flex-col sm:flex-row gap-4 justify-center items-center text-center lg:justify-start lg:items-start", !heroVisuals && " justify-center items-center")}
            >
              {children}
            </motion.div>
          )}
        </div>
        {heroVisuals &&
          <motion.div initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.4, ease: "easeOut" }}
            className="flex items-center justify-center">
            {heroVisuals}
          </motion.div>
        }
      </div>
    </section>
  );
};

export default Hero;
